import { useState, useCallback, useEffect } from 'react';
import { TagDetailsSection } from './TagDetailsSection';
import { TagDetailsCustomSummaryPrompts } from './TagDetailsCustomSummaryPrompts';
import { TagDetailsSectionEditButtons } from './TagDetailsSectionEditButtons';

export type TagDetailsCustomSummarySectionProps = {
  prompts: string[];
  onSave?: (prompts: string[]) => void | Promise<void>;
  isSaving?: boolean;
};

export const TagDetailsCustomSummarySection = ({
  prompts,
  onSave,
  isSaving = false,
}: TagDetailsCustomSummarySectionProps) => {
  const normalize = (p: string[]) => (p.length > 0 ? p : ['']);

  const [isEditing, setIsEditing] = useState(false);
  const [local, setLocal] = useState<string[]>(() => normalize(prompts));

  useEffect(() => {
    if (!isEditing) setLocal(normalize(prompts));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompts]);

  const handleEdit = useCallback(() => {
    setLocal(normalize(prompts));
    setIsEditing(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompts]);

  const handleCancel = useCallback(() => {
    setLocal(normalize(prompts));
    setIsEditing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompts]);

  const handleSave = useCallback(async () => {
    const filtered = local.map((p) => p.trim()).filter((p) => p.length > 0);
    await onSave?.(filtered);
    setIsEditing(false);
  }, [local, onSave]);

  const actions = onSave ? (
    <TagDetailsSectionEditButtons
      isEditing={isEditing}
      isSaving={isSaving}
      onEdit={handleEdit}
      onSave={handleSave}
      onCancel={handleCancel}
    />
  ) : undefined;

  return (
    <TagDetailsSection title="Custom Summary Prompt" actions={actions}>
      <TagDetailsCustomSummaryPrompts
        prompts={prompts}
        isEditing={isEditing}
        local={local}
        onLocalChange={setLocal}
      />
    </TagDetailsSection>
  );
};

TagDetailsCustomSummarySection.displayName = 'TagDetails.CustomSummarySection';
